import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Row,
  Col,
  Tooltip,
  DatePicker,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;

const SubscriptionTransactionManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    customerName: "",
    environmentType: "",
    paymentStatus: "",
    transactionType: "",
    start_date: "",
    end_date: "",
  });

  // Date range state
  const [dateRange, setDateRange] = useState([]);

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [transactionList, setTransactionList] = useState([]);

  // ✅ Summary state from API
  const [transactionSummary, setTransactionSummary] = useState({
    total_transactions: 0,
    total_success: 0,
    total_failed: 0,
    total_refunded: 0,
    total_revenue: 0,
  });

  // Debounce customer name for API calls
  const debouncedCustomerName = useDebounce(filters.customerName, 500);

  // Options from your model
  const PAYMENT_STATUS_OPTIONS = [
    { value: "SUCCESS", label: t("transactionManager.paymentStatusSuccess") },
    { value: "FAILED", label: t("transactionManager.paymentStatusFailed") },
    { value: "REFUNDED", label: t("transactionManager.paymentStatusRefunded") },
  ];

  const PAYMENT_METHOD_OPTIONS = [
    { value: "CARD", label: t("transactionManager.paymentMethodCard") },
    { value: "BANK", label: t("transactionManager.paymentMethodBank") },
    { value: "PAYPAL", label: t("transactionManager.paymentMethodPaypal") },
    { value: "OTHER", label: t("transactionManager.paymentMethodOther") },
  ];

  const TRANSACTION_TYPE_OPTIONS = [
    {
      value: "PURCHASE",
      label: t("transactionManager.transactionTypePurchase"),
    },
    { value: "RENEWAL", label: t("transactionManager.transactionTypeRenewal") },
    { value: "UPGRADE", label: t("transactionManager.transactionTypeUpgrade") },
    { value: "REFUND", label: t("transactionManager.transactionTypeRefund") },
  ];

  // Environment Type Options
  const ENVIRONMENT_TYPE_OPTIONS = [
    {
      value: "INDIVIDUAL",
      label: t("transactionManager.environmentIndividual"),
    },
    { value: "CLUB", label: t("transactionManager.environmentClub") },
    { value: "KNKV", label: t("transactionManager.environmentKNKV") },
  ];

  // Build API URL with ALL parameters including date filters
  const buildApiUrl = () => {
    let url = `${API_ENDPOINTS.SUBSCRIPTION_TRANSACTIONS_LIST}?page=${currentPage}&page_size=${pageSize}`;

    // Add search parameter
    if (debouncedSearchTerm) {
      url += `&search=${encodeURIComponent(debouncedSearchTerm)}`;
    }

    // Add ALL filter parameters
    if (filters.paymentStatus) {
      url += `&payment_status=${encodeURIComponent(filters.paymentStatus)}`;
    }

    if (filters.transactionType) {
      url += `&transaction_type=${encodeURIComponent(filters.transactionType)}`;
    }

    if (filters.environmentType) {
      url += `&environment_type=${encodeURIComponent(filters.environmentType)}`;
    }

    if (debouncedCustomerName) {
      url += `&customer_name=${encodeURIComponent(debouncedCustomerName)}`;
    }

    // Add date filters
    if (filters.start_date) {
      url += `&start_date=${encodeURIComponent(filters.start_date)}`;
    }

    if (filters.end_date) {
      url += `&end_date=${encodeURIComponent(filters.end_date)}`;
    }

    return url;
  };

  // Use GET hook with dynamic URL
  const apiUrl = buildApiUrl();
  const {
    execute: fetchTransactions,
    loading: loadingTransactions,
    error: fetchError,
  } = useGet(apiUrl, false);

  // Load data function – now extracts summary from API response
  const loadData = async () => {
    try {
      const response = await fetchTransactions();
      if (response) {
        setTransactionList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
        // ✅ Store summary data from API
        if (response.summary) {
          setTransactionSummary(response.summary);
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      Toast.error(error.message || t("common.error"));
    }
  };

  // Load data when page or pageSize changes
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize]);

  // Load data when filters or search changes (reset to page 1)
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [
    debouncedSearchTerm,
    debouncedCustomerName,
    filters.paymentStatus,
    filters.transactionType,
    filters.environmentType,
    filters.start_date,
    filters.end_date,
  ]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  };

  // Handle date range change
  const handleDateRangeChange = (dates, dateStrings) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
      setFilters((prev) => ({
        ...prev,
        start_date: dateStrings[0],
        end_date: dateStrings[1],
      }));
    } else {
      setDateRange([]);
      setFilters((prev) => ({
        ...prev,
        start_date: "",
        end_date: "",
      }));
    }
  };

  // Quick date range presets
  const rangePresets = [
    {
      label: t("transactionManager.last7Days"),
      value: [dayjs().subtract(7, "d"), dayjs()],
    },
    {
      label: t("transactionManager.last30Days"),
      value: [dayjs().subtract(30, "d"), dayjs()],
    },
    {
      label: t("transactionManager.thisMonth"),
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: t("transactionManager.lastMonth"),
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: t("transactionManager.thisYear"),
      value: [dayjs().startOf("year"), dayjs().endOf("year")],
    },
  ];

  // Reset filters
  const resetFilters = () => {
    setFilters({
      customerName: "",
      environmentType: "",
      paymentStatus: "",
      transactionType: "",
      start_date: "",
      end_date: "",
    });
    setDateRange([]);
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Use dynamic API hook for getting transaction details (keep for modal if needed)
  const { execute: getById, loading: loadingDetails } = useDynamicApi("GET");

  // Navigate to view page
  const openViewPage = (id) => {
    navigate(`/subscription-transactions/details/${id}`);
  };

  // Open modal for view (keeping for backward compatibility)
  const openViewModal = async (item) => {
    try {
      setSelectedTransaction(item);

      const endpoint = `${API_ENDPOINTS.SUBSCRIPTION_TRANSACTIONS_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedTransaction(response.data);
        setIsModalOpen(true);
      } else {
        Toast.error(t("transactionManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      Toast.error(error.message || t("transactionManager.failedFetchDetails"));
    }
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Format date for display (short version)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return "N/A";
    return new Intl.NumberFormat(i18n.language === "nl" ? "nl-NL" : "en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Get option labels
  const getPaymentStatusLabel = (status) => {
    const option = PAYMENT_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option ? option.label : status;
  };

  const getPaymentMethodLabel = (method) => {
    const option = PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === method);
    return option ? option.label : method;
  };

  const getTransactionTypeLabel = (type) => {
    const option = TRANSACTION_TYPE_OPTIONS.find((opt) => opt.value === type);
    return option ? option.label : type;
  };

  // ✅ Statistics now use summary from API (already filtered)
  const statistics = {
    total: transactionSummary.total_transactions || 0,
    success: transactionSummary.total_success || 0,
    failed: transactionSummary.total_failed || 0,
    refunded: transactionSummary.total_refunded || 0,
    totalAmount: transactionSummary.total_revenue || 0,
  };

  // FRS: Excel Export Function
  const handleExportExcel = async () => {
    try {
      // Create CSV content
      const headers = [
        t("transactionManager.transactionId"),
        t("transactionManager.dateTime"),
        t("transactionManager.customerName"),
        t("transactionManager.customerEmail"),
        t("transactionManager.subscriptionName"),
        t("transactionManager.amountPaid"),
        t("transactionManager.paymentStatus"),
        t("transactionManager.paymentMethod"),
        t("transactionManager.environmentType"),
        t("transactionManager.transactionType"),
      ];

      const csvRows = [
        headers.join(","),
        ...transactionList.map((item) =>
          [
            `"${item.transaction_id || ""}"`,
            `"${formatDateTime(item.transaction_date)}"`,
            `"${item.customer_name || ""}"`,
            `"${item.customer_email || ""}"`,
            `"${item.subscription_name || ""}"`,
            `"${item.amount_paid || ""}"`,
            `"${getPaymentStatusLabel(item.payment_status)}"`,
            `"${getPaymentMethodLabel(item.payment_method) || ""}"`,
            `"${item.environment_type || ""}"`,
            `"${getTransactionTypeLabel(item.transaction_type)}"`,
          ].join(","),
        ),
      ];

      const csvContent = csvRows.join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `subscription_transactions_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Toast.success(t("transactionManager.exportSuccess"));
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Toast.error(t("transactionManager.exportFailed"));
    }
  };

  const columns = [
    {
      title: t("transactionManager.transactionId"),
      dataIndex: "transaction_id",
      key: "transaction_id",
      render: (transactionId) => <strong>{transactionId || "N/A"}</strong>,
    },
    {
      title: t("transactionManager.dateTime"),
      key: "transaction_date",
      render: (_, record) => formatDateTime(record.transaction_date),
    },
    {
      title: t("transactionManager.customerName"),
      dataIndex: "customer_name",
      key: "customer_name",
      render: (name) => name || "N/A",
    },
    {
      title: t("transactionManager.subscriptionName"),
      dataIndex: "subscription_name",
      key: "subscription_name",
      render: (name) => name || "N/A",
    },
    {
      title: t("transactionManager.transactionType"),
      dataIndex: "transaction_type",
      key: "transaction_type",
      render: (type) => (
        <span className="transaction-type">
          {getTransactionTypeLabel(type)}
        </span>
      ),
    },
    {
      title: t("transactionManager.amount"),
      dataIndex: "amount_paid",
      key: "amount_paid",
      render: (amount) => (
        <span className="amount">{formatCurrency(amount)}</span>
      ),
    },
    {
      title: t("transactionManager.paymentStatus"),
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status) => (
        <Tag
          color={
            status === "SUCCESS"
              ? "green"
              : status === "FAILED"
                ? "red"
                : "orange"
          }
        >
          {getPaymentStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: t("transactionManager.paymentMethod"),
      dataIndex: "payment_method",
      key: "payment_method",
      render: (method) => getPaymentMethodLabel(method) || "N/A",
    },
    {
      title: t("transactionManager.environmentType"),
      dataIndex: "environment_type",
      key: "environment_type",
      render: (type) => (
        <span className="environment-badge">{type || "N/A"}</span>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Tooltip title={t("common.view")}>
          <Button
            className="btn-view"
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openViewPage(record.id)}
            disabled={loadingTransactions}
          />
        </Tooltip>
      ),
    },
  ];

  const hasFilters =
    searchTerm ||
    filters.customerName ||
    filters.environmentType ||
    filters.paymentStatus ||
    filters.transactionType ||
    filters.start_date ||
    filters.end_date;

  // Show active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filters.customerName) count++;
    if (filters.environmentType) count++;
    if (filters.paymentStatus) count++;
    if (filters.transactionType) count++;
    if (filters.start_date && filters.end_date) count++;
    return count;
  };

  return (
    <div className="subscription-transaction-page">
      <Card
        title={t("transactionManager.title")}
        extra={
          <Space>
            {hasFilters && (
              <Tag color="blue">
                {getActiveFiltersCount()}{" "}
                {t("transactionManager.activeFilters")}
              </Tag>
            )}
            <Button onClick={handleExportExcel} disabled={loadingTransactions}>
              📊 {t("transactionManager.exportToExcel")}
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Space
          wrap
          style={{ marginBottom: 16 }}
          className="transaction-filters"
        >
          <Input
            placeholder={t("transactionManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ width: 200 }}
            disabled={loadingTransactions}
          />
          <Input
            placeholder={t("transactionManager.customerNamePlaceholder")}
            name="customerName"
            value={filters.customerName}
            onChange={handleFilterChange}
            style={{ width: 160 }}
            allowClear
            disabled={loadingTransactions}
          />
          <Select
            placeholder={t("transactionManager.environmentType")}
            value={filters.environmentType || undefined}
            onChange={(v) => handleSelectChange("environmentType", v)}
            style={{ width: 160 }}
            allowClear
            disabled={loadingTransactions}
            options={ENVIRONMENT_TYPE_OPTIONS}
          />
          <Select
            placeholder={t("transactionManager.paymentStatus")}
            value={filters.paymentStatus || undefined}
            onChange={(v) => handleSelectChange("paymentStatus", v)}
            style={{ width: 160 }}
            allowClear
            disabled={loadingTransactions}
            options={PAYMENT_STATUS_OPTIONS}
          />
          <Select
            placeholder={t("transactionManager.transactionType")}
            value={filters.transactionType || undefined}
            onChange={(v) => handleSelectChange("transactionType", v)}
            style={{ width: 160 }}
            allowClear
            disabled={loadingTransactions}
            options={TRANSACTION_TYPE_OPTIONS}
          />

          {/* Date Range Picker */}
          <RangePicker
            placeholder={[
              t("transactionManager.startDate"),
              t("transactionManager.endDate"),
            ]}
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ width: 280 }}
            disabled={loadingTransactions}
            presets={rangePresets}
            format="YYYY-MM-DD"
          />

          {hasFilters && (
            <Button onClick={resetFilters} disabled={loadingTransactions}>
              {t("common.clearFilters")}
            </Button>
          )}
        </Space>

        {/* Statistics Cards – now using API summary data */}
        <Row
          gutter={16}
          style={{ marginBottom: 16 }}
          className="transaction-stats-row"
        >
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              size="small"
              title={t("transactionManager.totalTransactions")}
            >
              <span className="stat-number">{statistics.total}</span>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small" title={t("transactionManager.successful")}>
              <span className="stat-number" style={{ color: "#52c41a" }}>
                {statistics.success}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small" title={t("transactionManager.failed")}>
              <span className="stat-number" style={{ color: "#ff4d4f" }}>
                {statistics.failed}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small" title={t("transactionManager.refunded")}>
              <span className="stat-number" style={{ color: "#faad14" }}>
                {statistics.refunded}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small" title={t("transactionManager.totalRevenue")}>
              <span className="stat-number">
                {formatCurrency(statistics.totalAmount)}
              </span>
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <div className="table-responsive">
          <Table
            dataSource={transactionList}
            columns={columns}
            rowKey="id"
            loading={loadingTransactions}
            locale={{
              emptyText: hasFilters
                ? t("transactionManager.noMatchingTransactions")
                : t("transactionManager.noTransactionsFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${t("common.showing")} ${(currentPage - 1) * pageSize + 1} ${t("common.to")} ${Math.min(currentPage * pageSize, total)} ${t("common.of")} ${total} ${t("transactionManager.transactions")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* View Details Modal (Optional - can be removed if using separate page) */}
      {isModalOpen && selectedTransaction && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTransaction(null);
          }}
          title={t("transactionManager.transactionDetails")}
          showConfirm={false}
          size="large"
        >
          <div className="view-details">
            <div className="detail-row">
              <strong>{t("transactionManager.transactionId")}:</strong>
              <span>{selectedTransaction.transaction_id}</span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.transactionDate")}:</strong>
              <span>
                {formatDateTime(selectedTransaction.transaction_date)}
              </span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.customerName")}:</strong>
              <span>{selectedTransaction.customer_name || "N/A"}</span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.customerEmail")}:</strong>
              <span>{selectedTransaction.customer_email || "N/A"}</span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.subscriptionPackage")}:</strong>
              <span>{selectedTransaction.subscription_name || "N/A"}</span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.transactionType")}:</strong>
              <span className="transaction-type">
                {getTransactionTypeLabel(selectedTransaction.transaction_type)}
              </span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.amountPaid")}:</strong>
              <span className="amount">
                {formatCurrency(selectedTransaction.amount_paid)}
              </span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.paymentStatus")}:</strong>
              <span
                className={`status-badge ${
                  selectedTransaction.payment_status === "SUCCESS"
                    ? "success"
                    : selectedTransaction.payment_status === "FAILED"
                      ? "failed"
                      : "refunded"
                }`}
              >
                {getPaymentStatusLabel(selectedTransaction.payment_status)}
              </span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.paymentMethod")}:</strong>
              <span>
                {getPaymentMethodLabel(selectedTransaction.payment_method) ||
                  "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <strong>{t("transactionManager.environmentType")}:</strong>
              <span className="environment-badge">
                {selectedTransaction.environment_type || "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <strong>{t("common.createdAt")}:</strong>
              <span>{formatDateTime(selectedTransaction.created_at)}</span>
            </div>
            <div className="detail-row">
              <strong>{t("common.lastUpdated")}:</strong>
              <span>{formatDateTime(selectedTransaction.updated_at)}</span>
            </div>

            {/* Additional details if available */}
            {selectedTransaction.payment_reference && (
              <div className="detail-row">
                <strong>{t("transactionManager.paymentReference")}:</strong>
                <span>{selectedTransaction.payment_reference}</span>
              </div>
            )}

            {selectedTransaction.notes && (
              <div className="detail-row">
                <strong>{t("transactionManager.notes")}:</strong>
                <span>{selectedTransaction.notes}</span>
              </div>
            )}

            {selectedTransaction.invoice_number && (
              <div className="detail-row">
                <strong>{t("transactionManager.invoiceNumber")}:</strong>
                <span>{selectedTransaction.invoice_number}</span>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SubscriptionTransactionManager;
